import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import { MyPlanCard, PlanExpiryBanner } from "@/components/plan/PlanWidgets";
import { useMyPlan } from "@/lib/planDisplay";
import { OdiaMessageCard, OdiaMessagePopup } from "@/components/poshan/OdiaMessage";
import ReferralShare from "@/components/poshan/ReferralShare";
import { Button } from "@/components/ui/button";
import {
  CalendarDays, Settings2, Wallet, Utensils, FileSpreadsheet, LayoutGrid,
  MessageCircle, Download, Lock, ShieldCheck, Sparkles, Camera, Crown,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { useActiveSchool } from "@/lib/activeSchool";
import { LiveActivityWidget } from "@/components/LiveActivity";
import ModeOfWorkSelector from "@/components/poshan/ModeOfWorkSelector";
import SetupCompletionPanel from "@/components/poshan/SetupCompletionPanel";
import ActivationCountdown from "@/components/poshan/ActivationCountdown";
import AdminSetupFlowCard from "@/components/poshan/AdminSetupFlowCard";
import IncompleteSetupBanner from "@/components/poshan/IncompleteSetupBanner";
import AdminMessagePopup from "@/components/poshan/AdminMessagePopup";
import MobileWelcomeHero from "@/components/mobile/MobileWelcomeHero";
import { usePolling } from "@/lib/usePolling";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || "918093414633";

// Plan-key → feature flags so dashboard tiles can decide locked/unlocked.
const PLAN_FEATURES = {
  // ₹29 — MDCF Monthly. Daily Entry + Fund Management + Daily Consumption
  // unlocked because they FEED the monthly MDCF (no Daily Entry → no MDCF
  // data, so they must travel together).
  mdcf_monthly: ["mdm_bills", "mdcf_monthly_odia", "mdcf_monthly_english"],
  mdcf_yearly: ["mdm_bills", "mdcf_monthly_odia", "mdcf_monthly_english", "mdcf_yearly_access", "auto_filled_school_data"],
  all_reports_yearly: [
    "mdcf_monthly_odia", "mdcf_monthly_english", "mdcf_yearly_access",
    "auto_filled_school_data", "mdm_bills", "hand_receipts", "uc_reports",
    "all_registers", "all_reports_access", "priority_future_features",
  ],
};
// Legacy plan keys (existing users) — grandfather full access so they don't get locked out.
const LEGACY_PLANS = new Set(["mdm_only", "mdcf_annual", "all_reports_annual", "admin_extended", "free_admin_3mo", "free_self_3mo", "self_yearly", "self_quarterly", "self_monthly", "bills_uc_vouchers"]);

// iter152 — Core-workflow tiles (Daily Entry, Fund Management, Daily
// Consumption, Monthly MDCF) must be UNLOCKED for every paid plan. These
// are prerequisite screens that FEED the paid reports — locking them
// creates a chicken-and-egg where the user can't produce data to enjoy
// their subscription. Any plan key with a non-empty features array (i.e.
// a real paid plan the user checked out) qualifies.
const CORE_WORKFLOW_FEATURES = new Set([
  "mdm_bills", "mdcf_monthly_odia", "mdcf_monthly_english",
]);

function hasFeature(plan, feature) {
  if (!plan) return false;
  if (LEGACY_PLANS.has(plan)) return true; // grandfather
  if (CORE_WORKFLOW_FEATURES.has(feature)) return true; // any active plan → core workflow unlocked
  return (PLAN_FEATURES[plan] || []).includes(feature);
}

function whatsappUrl(user, opts = {}) {
  const reason = opts.reason || "Setup help";
  const text = encodeURIComponent(
    `Namaste, please help with ${reason} for my school.\n\n` +
    `UDISE: ${user?.udiseCode || ""}\n` +
    `School: ${user?.schoolName || ""}\n` +
    `Category: ${user?.schoolCategory || ""}\n` +
    `Mobile: ${user?.mobile || ""}\n\n` +
    `Kindly proceed.`,
  );
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;
}

// Quick-links + feature flag requirements per tile.
const QUICK_LINKS = [
  { to: "/daily", icon: CalendarDays, label: "Daily Entry", desc: "Record per-day meals & ingredients", feature: "mdm_bills" },
  { to: "/funds", icon: Wallet, label: "Fund Management", desc: "Class-wise rice / fund ledger", feature: "mdm_bills" },
  { to: "/consumption", icon: Utensils, label: "Daily Consumption", desc: "Print landscape day-wise report", feature: "mdm_bills" },
  { to: "/mdcf", icon: FileSpreadsheet, label: "Monthly MDCF", desc: "Auto-fill bills (English + Odia)", feature: "mdcf_monthly_odia" },
  { to: "/", icon: LayoutGrid, label: "Quarterly Reports", desc: "OGFR-23, OGFR-24, Hand Receipts", feature: "uc_reports" },
  { to: "/photo-bills", icon: Camera, label: "Bill Photos", desc: "Send daily-enrolment photos → get reports on WhatsApp", feature: null }, // always unlocked
  { to: "/setup", icon: Settings2, label: "Manage Setup", desc: "Vendors / CCHs / Year-wise enrolment", feature: null }, // always unlocked
];

export default function SchoolDashboard() {
  const { user, refreshMe } = useAuth();
  const { setActiveId } = useActiveSchool();
  const navigate = useNavigate();
  const [justVerified, setJustVerified] = useState(false);
  const prevVerifiedRef = useRef(user?.verified || false);

  useEffect(() => {
    if (user?.schoolId) setActiveId(user.schoolId);
  }, [user, setActiveId]);

  // Detect verified flip false → true → celebrate then redirect to /choose-plan
  // (only if user has NO active plan yet — new flow).
  useEffect(() => {
    const prev = prevVerifiedRef.current;
    if (!prev && user?.verified) {
      setJustVerified(true);
      const planKey = (user.activePlan || "").trim();
      // New user (no plan) → mandatory plan picker after a brief celebration.
      if (!planKey) {
        const t = setTimeout(() => navigate("/choose-plan"), 3500);
        return () => clearTimeout(t);
      }
      const t = setTimeout(() => setJustVerified(false), 6000);
      return () => clearTimeout(t);
    }
    prevVerifiedRef.current = user?.verified || false;
  }, [user, navigate]);

  // Poll /me while unverified so the moment scheduler activates the user,
  // the dashboard auto-advances. Tighter polling than before since the
  // 1-hour timer makes user actively wait.
  // iter150-review — poll /me every 60 s (was 15 s) and only while
  // the tab is visible + online (via usePolling). The initial fetch
  // happens on login via AuthContext, so slower polling here is fine.
  usePolling(refreshMe, 60_000, !!user && !user.verified);

  const setupChoice = user?.setupChoice || user?.setupMode || "";
  const verified = !!user?.verified;
  const setupCompleteAt = user?.setupCompleteAt;
  const scheduledActivationAt = user?.scheduledActivationAt;
  const activePlan = (user?.activePlan || "").trim();
  const planInfo = useMyPlan(user?.role === "school" ? user.id : null);
  const adminSetupPaid = !!user?.adminSetupPaid;

  // -------- STAGE DETECTION --------
  // Stage 1: No setup mode chosen → ModeOfWorkSelector
  // Stage 2a (admin paid): setupChoice="admin" + paid + !verified → waiting for admin team
  // Stage 2b (self): setupChoice="self" + !setupCompleteAt + !verified → SetupCompletionPanel
  // Stage 3: setupCompleteAt + !verified → ActivationCountdown
  // Stage 4: verified + no activePlan → redirect to /choose-plan (via effect above)
  // Stage 5: verified + activePlan → main dashboard with feature gating

  const stage = (() => {
    if (!setupChoice) return "choose-mode";
    if (!verified) {
      if (setupChoice === "admin") {
        if (setupCompleteAt) return "countdown";
        return adminSetupPaid ? "admin-waiting" : "admin-payment-pending";
      }
      // self mode
      if (setupCompleteAt) return "countdown";
      return "setup-completion";
    }
    if (!activePlan) return "verified-needs-plan";
    return "dashboard";
  })();

  return (
    <main className="min-h-screen bg-sand-50 text-ink-900">
      <Navbar />
      <div className="mx-auto max-w-7xl px-3 py-6 sm:px-6 sm:py-8 lg:px-8">
        {/* iter136 — gradient blue mobile hero (md:hidden) for that
            polished Android-app top-strip. The plain text header below
            stays for desktop. */}
        <MobileWelcomeHero
          schoolName={user?.schoolName || "School Dashboard"}
          subtitle={(user?.udiseCode || "").startsWith("PENDING-")
            ? `Finish setup to publish your UDISE · ${user?.schoolCategory || ""}`
            : `UDISE ${user?.udiseCode || "—"} · ${user?.schoolCategory || ""}`}
          accent={planInfo?.planLabel ? `Plan · ${planInfo.planLabel}` : null}
        />
        <PlanExpiryBanner />
        {user?.verified && <MyPlanCard />}

        {/* iter158 — PENDING-UDISE soft prompt. Shown for teachers whose
            accounts were created by the Partner Grant API (toolkit_combo)
            or anyone who registered before supplying a real UDISE. The
            banner is non-blocking — no route is forced; the user can
            dismiss by tapping "Fill UDISE" and going to Setup. */}
        {(user?.udiseCode || "").startsWith("PENDING-") && (
          <section
            className="mb-6 rounded-2xl border-2 border-ochre/40 bg-ochre/10 p-4"
            data-testid="pending-udise-banner"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <Sparkles className="mt-0.5 h-5 w-5 text-ochre-dark" />
                <div>
                  <p className="font-heading text-sm font-bold text-ink-900">
                    Add your school's UDISE to unlock reports · UDISE ଯୋଡ଼ନ୍ତୁ
                  </p>
                  <p className="mt-0.5 text-xs text-ink-700">
                    Your PRO is active. Fill the real UDISE (11 digits) once and we'll
                    personalise every PDF, UC and ledger with your school's name.
                  </p>
                </div>
              </div>
              <a href="/setup">
                <Button size="sm" className="bg-ochre text-white hover:bg-ochre-dark" data-testid="pending-udise-fill-btn">
                  Fill UDISE
                </Button>
              </a>
            </div>
          </section>
        )}

        {/* Welcome header (desktop only — mobile uses MobileWelcomeHero above) */}
        <section className="mb-6 hidden md:block">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-ink-700">Welcome</p>
          <h1 className="mt-2 font-heading text-2xl font-semibold tracking-tight sm:mt-3 sm:text-3xl lg:text-4xl">{user?.schoolName || "School Dashboard"}</h1>
          <p className="mt-2 text-sm text-ink-700">
            {(user?.udiseCode || "").startsWith("PENDING-")
              ? <>UDISE pending · {user?.schoolCategory}</>
              : <>UDISE <b>{user?.udiseCode}</b> · {user?.schoolCategory}</>}
          </p>
        </section>

        {/* Just-verified celebration */}
        {justVerified && (
          <section className="mb-6 animate-pulse rounded-2xl border-2 border-emerald-400 bg-gradient-to-r from-emerald-100 to-emerald-50 p-5 text-emerald-900 shadow" data-testid="just-verified-banner">
            <div className="flex items-center gap-3">
              <Sparkles className="h-7 w-7 text-emerald-600" />
              <div>
                <h2 className="text-lg font-bold">🎉 Your account is now activated!</h2>
                <p className="text-sm">
                  {activePlan
                    ? "All your subscribed features are unlocked below."
                    : "Redirecting you to choose your plan…"}
                </p>
              </div>
            </div>
          </section>
        )}

        {/* STAGE 1 — Choose setup mode */}
        {stage === "choose-mode" && (
          <ModeOfWorkSelector
            user={user}
            onChoose={async () => { await refreshMe(); }}
          />
        )}

        {/* STAGE 2a — Admin paid, awaiting team completion */}
        {stage === "admin-waiting" && (
          <AdminSetupFlowCard user={user} />
        )}

        {/* STAGE 2a-fallback — admin chosen but payment not completed yet */}
        {stage === "admin-payment-pending" && (
          <section className="mb-8 rounded-2xl border-2 border-rose-300 bg-rose-50 p-6" data-testid="admin-payment-pending-card">
            <h2 className="text-xl font-semibold text-rose-900">⚠️ Payment incomplete</h2>
            <p className="mt-2 text-sm text-rose-900/80">
              Your School Setup by Admin payment didn't complete. Please try again or switch to Self Setup (Free).
            </p>
            <div className="mt-4 flex gap-3">
              <Button onClick={() => refreshMe()} className="bg-rose-600 hover:bg-rose-700 text-white">
                Refresh status
              </Button>
            </div>
          </section>
        )}

        {/* STAGE 2b — Self setup, fill missing fields */}
        {stage === "setup-completion" && (
          <SetupCompletionPanel user={user} onMarkedComplete={async () => { await refreshMe(); }} />
        )}

        {/* STAGE 3 — 1-hour countdown to auto-verification */}
        {stage === "countdown" && (
          <ActivationCountdown
            scheduledAt={scheduledActivationAt}
            onRefresh={() => refreshMe()}
          />
        )}

        {/* STAGE 4 — verified but needs plan (handled by useEffect redirect) */}
        {stage === "verified-needs-plan" && (
          <section className="mb-8 rounded-2xl border-2 border-purple-300 bg-purple-50 p-8 text-center" data-testid="needs-plan-card">
            <Crown className="mx-auto h-12 w-12 text-purple-600" />
            <h2 className="mt-4 text-2xl font-bold text-purple-900">Choose your plan to unlock reports</h2>
            <p className="mt-2 text-sm text-purple-800">Your account is activated. Pick a subscription to start using reports.</p>
            <Button onClick={() => navigate("/choose-plan")} className="mt-5 bg-purple-600 hover:bg-purple-700 text-white" data-testid="goto-choose-plan-btn">
              View Plans
            </Button>
          </section>
        )}

        {/* STAGE 5 — Full dashboard for verified + subscribed users */}
        {stage === "dashboard" && (
          <>
            {/* iter114 — verified users with INCOMPLETE setup see a section-wise nudge banner. */}
            <IncompleteSetupBanner verified onMarkedComplete={async () => { await refreshMe(); }} />

            <section className="mb-6 rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-5" data-testid="verified-banner">
              <div className="flex flex-wrap items-start gap-3">
                <ShieldCheck className="mt-0.5 h-6 w-6 text-emerald-600 shrink-0" />
                <div className="flex-1">
                  <h2 className="text-base font-semibold text-emerald-900">
                    Verified · Plan: <b>{planInfo?.planLabel || "Active"}</b>
                  </h2>
                  <p className="mt-1 text-sm text-emerald-800">
                    All your subscribed features are unlocked below.
                  </p>
                </div>
                <a href={whatsappUrl(user, { reason: "general help" })} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700" data-testid="contact-support-whatsapp-link">
                  <MessageCircle className="h-4 w-4" /> Contact Support
                </a>
              </div>
            </section>

            <section className="mb-6" data-testid="odia-message-section">
              <OdiaMessageCard />
            </section>

            <section className="mb-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="quick-links">
              {QUICK_LINKS.map((q) => {
                const featureOk = !q.feature || hasFeature(activePlan, q.feature);
                if (!featureOk) {
                  return (
                    <Link
                      key={q.to}
                      to="/choose-plan"
                      className="group relative rounded-xl border-2 border-amber-200 bg-white/80 p-5 shadow-sm transition hover:border-amber-400 hover:shadow-md"
                      data-testid={`quick-link-locked-${q.to.replace(/\//g, "") || "home"}`}
                      title="Upgrade your plan to unlock"
                    >
                      <Lock className="absolute right-3 top-3 h-4 w-4 text-amber-600" />
                      <q.icon className="h-7 w-7 text-amber-600 opacity-70" />
                      <h3 className="mt-3 font-semibold text-amber-900">{q.label}</h3>
                      <p className="mt-1 text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-amber-700">
                        Locked · Upgrade plan →
                      </p>
                    </Link>
                  );
                }
                return (
                  <Link key={q.to} to={q.to} className="group rounded-xl border border-border bg-white p-5 shadow-sm transition hover:border-clay hover:shadow-md" data-testid={`quick-link-${q.to.replace("/", "") || "home"}`}>
                    <q.icon className="h-7 w-7 text-clay" />
                    <h3 className="mt-3 font-semibold">{q.label}</h3>
                    <p className="mt-1 text-xs text-ink-700">{q.desc}</p>
                  </Link>
                );
              })}
            </section>

            <section className="rounded-2xl border border-border bg-white p-6 shadow-sm">
              <h2 className="flex items-center gap-2 text-lg font-semibold"><Download className="h-5 w-5 text-clay" /> Where to download reports</h2>
              <ul className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                <li><b>Quarterly UCs (OGFR-23 / 24)</b> — Quarterly Reports → "Print" button.</li>
                <li><b>Hand Receipts (Odia)</b> — Quarterly Reports → "Hand Receipts" tab → "Print".</li>
                <li><b>Monthly MDCF (English + Odia)</b> — Monthly MDCF → choose month → Print.</li>
                <li><b>Daily Consumption Report</b> — Daily Consumption → CSV / Print (landscape).</li>
                <li><b>Fund Management Ledger</b> — Fund Management → CSV / Word / Print.</li>
                <li><b>Year-wise Enrolment Snapshot</b> — Setup → Year-wise Enrolment → Print.</li>
              </ul>
              <Button asChild variant="outline" className="mt-5">
                <Link to="/" data-testid="cta-quarterly-link">Open Quarterly Reports</Link>
              </Button>
            </section>

            <section className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <ReferralShare user={user} />
              </div>
              <div>
                <LiveActivityWidget />
              </div>
            </section>
          </>
        )}

        {/* Referral share — visible to UNVERIFIED users too at the bottom. */}
        {!verified && (
          <section className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <ReferralShare user={user} />
            </div>
            <div>
              <LiveActivityWidget />
            </div>
          </section>
        )}
      </div>
      {/* Once-per-session welcome popup for verified users. */}
      {verified && <OdiaMessagePopup />}
      <AdminMessagePopup />
    </main>
  );
}
